import React, { FC, useState, useEffect, Fragment } from 'react';
import {
  Route,
  Switch,
  useParams,
  useHistory,
  useRouteMatch,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import 'react-quill/dist/quill.snow.css';

import { format } from 'date-fns';
import { TextField } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import SearchIcon from '@material-ui/icons/Search';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';

import { sel_jobData } from 'store/jobs/selectors';
import { getJob } from 'store/jobs/thunks';
import { deleteCandidate } from 'store/candidates/thunks';

import { filtersData } from './constants';
import { getRole } from 'utils/helpers';
import { ICandidate, IInterviews } from 'modules/Candidates/typings';
import { IJobRead } from 'modules/Jobs/typings';
import dompurify from 'dompurify';
import Filters from './components/Filters';
import HiringTeam from './components/HiringTeam';
import JobDetails from './components/JobDetails';
import JobHeroHeader from './components/JobHeroHeader';
import KCheckbox from 'components/KCheckbox';
import KModal from 'components/KModalCard';
import KTable from 'components/KTable';
import styles from './SingleJob.module.scss';
import { sessionData } from 'utils/localStorage';
import { useTranslation } from 'react-i18next';

interface ICandidateShow {
  id: string;
  name: string;
  recruiter: string;
  created: string;
  stage?: string;
  recruiterId: string;
  processInterviews?: IInterviews[];
}
interface IFilter {
  label: string;
  name: string;
  total: number;
}

interface HeadCell {
  disablePadding: boolean;
  id: string;
  label: string;
  numeric: boolean;
  options?: string;
  sortable?: boolean;
}

const headCells: HeadCell[] = [
  {
    id: 'name',
    numeric: false,
    disablePadding: false,
    label: 'Candidate',
  },
  {
    id: 'recruiter',
    numeric: false,
    disablePadding: false,
    label: 'Recruiter',
  },
  {
    id: 'created',
    numeric: false,
    disablePadding: false,
    label: 'Created',
  },
  {
    id: 'stage',
    numeric: false,
    disablePadding: false,
    label: 'Stage',
  },
  {
    id: 'options',
    numeric: false,
    disablePadding: false,
    label: ' ',
  },
];

const updateFiltersTotals = (
  filtersList: IFilter[],
  candidatesList: ICandidateShow[],
) => {
  const updatedFiltersList = filtersList.map((filter: IFilter) => {
    if (filter.name === 'All') {
      return {
        ...filter,
        total: candidatesList.length,
      };
    }
    return {
      ...filter,
      total: candidatesList.filter(
        (candidate) => candidate.stage === filter.name,
      ).length,
    };
  });

  return updatedFiltersList;
};

interface ICandidateShow {
  id: string;
  name: string;
  recruiter: string;
  created: string;
  stage?: string;
  recruiterId: string;
  processInterviews?: IInterviews[];
}
export interface ITab {
  tab: string;
  to: string;
}

interface ParamTypes {
  jobId: string;
}

const SingleJob: FC = () => {
  const [candidates, setCandidates] = useState<ICandidateShow[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('All');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState<string>('');
  const [showMyCandidates, setShowMyCandidates] = useState(false);
  const { jobId } = useParams<ParamTypes>();
  const match = useRouteMatch();
  const history = useHistory();
  const currentJob: IJobRead = useSelector(sel_jobData);
  const dispatch = useDispatch();
  const parsedId = jobId;

  const tabs: ITab[] = [
    { tab: 'CANDIDATES', to: `${match.url}` },
    { tab: 'JOB DETAILS', to: `${match.url}/details` },
    { tab: 'DESCRIPTION', to: `${match.url}/description` },
  ];

  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getJob(parsedId || ''));
  }, [dispatch, parsedId]);

  useEffect(() => {
    if (currentJob.candidates.length > 0) {
      const candidates = currentJob.candidates.map(
        (candidate: ICandidate) => {
          return {
            recruiterId:
              candidate.recruiter?.id ||
              (t('Not assigned') as string),
            id: candidate.id,
            name: `${candidate.firstName} ${candidate.lastName}`,
            recruiter: candidate.recruiter
              ? `${candidate.recruiter.firstName} ${candidate.recruiter.lastName}`
              : t('Not assigned'),
            created: format(
              new Date(candidate.createdAt),
              'dd/MM/yy',
            ),
            processInterviews: candidate.processInterviews
              ? candidate.processInterviews
              : [],
            stage: candidate.stage?.name,
          };
        },
      );

      setCandidates(candidates);
    } else {
      setCandidates([]);
    }
  }, [currentJob, t]);

  const user = sessionData.getUser()?.user;

  const filteredCandidatesListByUserId = candidates.filter(
    (candidate) => {
      if (showMyCandidates) {
        if (candidate.recruiterId === user?.id) {
          return true;
        } else if (candidate.processInterviews?.length) {
          return candidate.processInterviews.find((interview) => {
            if (interview.users?.length) {
              return interview.users.find(
                (interviewer) => interviewer.id === user?.id,
              );
            }
            return null;
          });
        }
      }
      return true;
    },
  );
  const filteredCandidatesListBySearch = filteredCandidatesListByUserId.filter(
    (candidate) =>
      candidate.name
        .toLowerCase()
        .replace(/^\s+|\s+$/gm, '')
        .includes(search.toLowerCase().replace(/^\s+|\s+$/gm, '')),
  );
  const filteredCandidatesListByStage =
    filter === 'All'
      ? filteredCandidatesListBySearch
      : filteredCandidatesListBySearch.filter(
          (candidate) => candidate.stage === filter,
        );
  const filteredCandidatesList = filteredCandidatesListByStage;

  const filters = updateFiltersTotals(
    filtersData,
    filteredCandidatesListByUserId,
  );

  const handleSearch = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearch(event.target.value);
  };

  const handleDelete = async () => {
    if (!currentId) return;
    await dispatch(deleteCandidate(currentId));
    setOpen(false);
    history.push('/candidates');
  };

  const handleOpen = (id: string) => {
    setOpen(true);
    setCurrentId(id);
  };

  const handleClose = () => {
    setCurrentId('0');
    setOpen(false);
  };

  const options = ['ADMINISTRATOR', 'RECRUITER'].includes(getRole())
    ? {
        view: '/candidates/',
        delete: (id: string) => {
          handleOpen(id);
        },
        edit: '/candidates/edit/',
      }
    : { view: '/candidates/' };

  const detailsData = {
    clientJobId: currentJob.clientJobId,
    clientName: currentJob.clientName,
    hiringManagers: [],
    id: currentJob.id,
    jobId: currentJob.jobId,
    jobCreator: currentJob.jobCreator
      ? `${currentJob.jobCreator.firstName} ${currentJob.jobCreator.lastName}`
      : t('Not assigned'),
    jobDepartment: currentJob.department?.name,
    jobSeniority: currentJob.jobSeniority,
    jobTime: currentJob.jobTime,
    jobType: currentJob.jobType,
    jobUrgency: currentJob.jobUrgency,
    location: currentJob.location,
    salaryCurrency: currentJob.salaryCurrency,
    salaryGross: currentJob.salaryGross,
    salaryLower: currentJob.salaryLower,
    salaryPeriod: currentJob.salaryPeriod,
    salaryUpper: currentJob.salaryUpper,
    status: currentJob.status,
    title: currentJob.title,
    createdAt: currentJob.createdAt,
    updatedAt: currentJob.updatedAt,
    openAt: currentJob.openAt,
    closedAt: currentJob.closedAt,
    requiredPositions: currentJob.requiredPositions,
    hiredCandidates: currentJob.hiredCandidates,
  };

  const jobCandidateTab = () => {
    return (
      <Fragment>
        <Grid item xs={12} md={2}>
          <Filters
            activeFilter={filter}
            changeFilter={(filterName) => setFilter(filterName)}
            filters={filters}
            location="All Locations"
          />
        </Grid>

        <Grid item container xs={12} md={10}>
          <div className={styles.tableContainer}>
            <Grid
              container
              alignItems="flex-end"
              spacing={2}
              item
              xs={12}
              md={8}
              className={styles.searchbar}
            >
              <Grid item>
                <SearchIcon />
              </Grid>
              <Grid item>
                <TextField
                  className={styles.searchCandidate}
                  label={t('Search for a candidate...')}
                  onChange={handleSearch}
                  value={search}
                />
              </Grid>
              <Grid item>
                <KCheckbox
                  onClick={() => {
                    setShowMyCandidates((state) => !state);
                  }}
                  tab={false}
                  value={showMyCandidates}
                />
              </Grid>
              <Grid item>
                <div className={styles.checkboxText}>
                  {t('My Candidates')}
                </div>
              </Grid>
            </Grid>
            <Grid item>
              <KTable
                defaultOrder={'created'}
                data={filteredCandidatesList}
                filterData={search}
                headCells={headCells}
                options={options}
              />
            </Grid>
          </div>
        </Grid>
      </Fragment>
    );
  };

  const jobDetailTab = () => {
    return (
      <Fragment>
        <Grid item xs={12} md={7}>
          <JobDetails details={detailsData} />
        </Grid>
        <Grid item xs={false} md={1}></Grid>
        <Grid item xs={12} md={4}>
          <HiringTeam
            managers={currentJob.hiringManagers}
            externalManager={currentJob.externalManager}
          />
        </Grid>
      </Fragment>
    );
  };

  const jobDescriptionTab = () => {
    return (
      <Paper className={styles.singleJobDescription}>
        <div className={styles.cardHeader}>
          <Typography variant="h5">{t('Description')}</Typography>
          <IconButton
            aria-label="edit"
            style={{ marginRight: '-12px' }}
            onClick={() => {
              history.push(`/jobs/edit/${detailsData.id}`);
            }}
          >
            <EditIcon />
          </IconButton>
        </div>
        <Grid item xs={12}>
          <div
            className="ql-editor"
            style={{ padding: 0 }}
            dangerouslySetInnerHTML={{
              __html: dompurify.sanitize(currentJob.details),
            }}
          />
        </Grid>
      </Paper>
    );
  };

  return (
    <Grid container>
      <KModal
        open={open}
        onClose={handleClose}
        type={'confirmation'}
        confirmFn={handleDelete}
        confirmMsg={t(
          'You will not be able to recover this candidate!',
        )}
        confirmBtn={t('Delete')}
        setOpenModal={setOpen}
      />

      <Grid item xs={12}>
        <JobHeroHeader jobData={currentJob} tabs={tabs} />
      </Grid>

      <Grid item xs={12}>
        <Grid container className={styles.mainContainer}>
          <Switch>
            <Route exact path={`${match.url}`}>
              {jobCandidateTab()}
            </Route>

            <Route exact path={`${match.url}/details`}>
              {jobDetailTab()}
            </Route>

            <Route exact path={`${match.url}/description`}>
              {jobDescriptionTab()}
            </Route>
          </Switch>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SingleJob;
